/**
 * Minimal read-only ZIP reader that never loads the whole archive.
 *
 * Only the central directory (a few KB at the end of the file) is read into
 * memory. Each entry is then served as a slice of the original File/Blob, so
 * a stored (uncompressed) entry costs no copy at all and a deflated one is
 * inflated as a stream. Shared by the page and the service worker.
 */

export interface ZipEntry {
  /** Path inside the archive, always with forward slashes. */
  name: string;
  /** 0 = stored, 8 = deflate. */
  method: number;
  compressedSize: number;
  size: number;
  localHeaderOffset: number;
}

const EOCD_SIG = 0x06054b50;
const ZIP64_LOCATOR_SIG = 0x07064b50;
const ZIP64_EOCD_SIG = 0x06064b50;
const CENTRAL_SIG = 0x02014b50;
const LOCAL_SIG = 0x04034b50;
const MAX_EOCD_SEARCH = 22 + 0xffff;

async function readBytes(blob: Blob, start: number, end: number): Promise<DataView> {
  return new DataView(await blob.slice(start, end).arrayBuffer());
}

function u64(view: DataView, offset: number): number {
  return view.getUint32(offset, true) + view.getUint32(offset + 4, true) * 0x100000000;
}

/** Reads the archive's central directory. Throws if the blob is not a ZIP. */
export async function readZipIndex(blob: Blob): Promise<Map<string, ZipEntry>> {
  const tailStart = Math.max(0, blob.size - MAX_EOCD_SEARCH);
  const tail = await readBytes(blob, tailStart, blob.size);
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === EOCD_SIG) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error("This file is not a ZIP / .walk archive.");

  let count = tail.getUint16(eocd + 10, true);
  let cdSize = tail.getUint32(eocd + 12, true);
  let cdOffset = tail.getUint32(eocd + 16, true);

  if (count === 0xffff || cdSize === 0xffffffff || cdOffset === 0xffffffff) {
    const locator = eocd - 20;
    if (locator >= 0 && tail.getUint32(locator, true) === ZIP64_LOCATOR_SIG) {
      const zip64Offset = u64(tail, locator + 8);
      const z = await readBytes(blob, zip64Offset, zip64Offset + 56);
      if (z.getUint32(0, true) !== ZIP64_EOCD_SIG) throw new Error("Damaged ZIP64 archive.");
      count = u64(z, 32);
      cdSize = u64(z, 40);
      cdOffset = u64(z, 48);
    }
  }

  const cd = await readBytes(blob, cdOffset, cdOffset + cdSize);
  const decoder = new TextDecoder();
  const entries = new Map<string, ZipEntry>();
  let p = 0;
  for (let n = 0; n < count && p + 46 <= cd.byteLength; n++) {
    if (cd.getUint32(p, true) !== CENTRAL_SIG) throw new Error("Damaged ZIP directory.");
    const flags = cd.getUint16(p + 8, true);
    const method = cd.getUint16(p + 10, true);
    let compressedSize = cd.getUint32(p + 20, true);
    let size = cd.getUint32(p + 24, true);
    const nameLen = cd.getUint16(p + 28, true);
    const extraLen = cd.getUint16(p + 30, true);
    const commentLen = cd.getUint16(p + 32, true);
    let localHeaderOffset = cd.getUint32(p + 42, true);
    const name = decoder
      .decode(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen))
      .replace(/\\/g, "/");

    // ZIP64 extra field: only the fields that overflowed are present, in this order.
    let e = p + 46 + nameLen;
    const extraEnd = e + extraLen;
    while (e + 4 <= extraEnd) {
      const id = cd.getUint16(e, true);
      const len = cd.getUint16(e + 2, true);
      if (id === 0x0001) {
        let q = e + 4;
        if (size === 0xffffffff) { size = u64(cd, q); q += 8; }
        if (compressedSize === 0xffffffff) { compressedSize = u64(cd, q); q += 8; }
        if (localHeaderOffset === 0xffffffff) { localHeaderOffset = u64(cd, q); }
      }
      e += 4 + len;
    }
    p = extraEnd + commentLen;

    if (name.endsWith("/") || name.startsWith("__MACOSX/")) continue;
    if (flags & 0x1) throw new Error("Password-protected ZIPs are not supported.");
    if (method !== 0 && method !== 8) {
      throw new Error(`Unsupported compression in ${name}. Re-zip with standard settings.`);
    }
    entries.set(name, { name, method, compressedSize, size, localHeaderOffset });
  }
  return entries;
}

/** Returns the entry's uncompressed bytes as a stream (stored: a plain slice). */
export async function openEntry(blob: Blob, entry: ZipEntry): Promise<ReadableStream<Uint8Array>> {
  const header = await readBytes(blob, entry.localHeaderOffset, entry.localHeaderOffset + 30);
  if (header.getUint32(0, true) !== LOCAL_SIG) throw new Error(`Damaged ZIP entry: ${entry.name}`);
  const start = entry.localHeaderOffset + 30 + header.getUint16(26, true) + header.getUint16(28, true);
  const data = blob.slice(start, start + entry.compressedSize);
  if (entry.method === 0) return data.stream();
  return data.stream().pipeThrough(new DecompressionStream("deflate-raw" as CompressionFormat));
}

/** Convenience for small entries (walk.json): reads and parses JSON. */
export async function readEntryJson<T>(blob: Blob, entry: ZipEntry): Promise<T> {
  return (await new Response(await openEntry(blob, entry)).json()) as T;
}
